/**
 * Lot Status & Validation
 *
 * Utilities for checking lot status (closed, dead meat, etc.)
 * @module portal/modules/lotOperations/status
 */

import { Lot } from '../types';
import { isLotClosed as utilIsLotClosed } from '../utils';

/**
 * Parse a date string for sorting purposes.
 * If the date has no time component, assume 11:59 PM for sorting.
 */
function parseDateForSorting(dateStr: string): number | null {
  if (typeof dateStr !== 'string' || !dateStr.trim()) return null;

  // Try parsing as-is first
  const asIs = Date.parse(dateStr);
  if (Number.isFinite(asIs)) {
    // Check if this is a date-only value (YYYY-MM-DD format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      // Set to 11:59:59.999 PM on that date for sorting purposes
      const date = new Date(asIs);
      date.setHours(23, 59, 59, 999);
      return date.getTime();
    }
    return asIs;
  }

  return null;
}

/**
 * Get effective close time for a lot
 */
export function getEffectiveCloseTime(lot: Lot): number {
  // If we have a numeric closeAt, use it
  if (typeof lot.closeAt === 'number' && Number.isFinite(lot.closeAt)) {
    return lot.closeAt;
  }

  // For lots with live finish date range, try to parse closeDateTime
  if ((lot.liveFinish || lot.liveFinishDateRange) && lot.closeDateTime) {
    const closeStr =
      typeof lot.closeDateTime === 'string'
        ? lot.closeDateTime
        : lot.closeDateTime?.iso || lot.closeDateTime?.raw;
    const parsed = closeStr ? parseDateForSorting(closeStr) : null;
    if (parsed) return parsed;
  }

  // Try parsing closeDateTime as-is
  if (lot.closeDateTime) {
    const closeStr =
      typeof lot.closeDateTime === 'string'
        ? lot.closeDateTime
        : lot.closeDateTime?.iso || lot.closeDateTime?.raw;
    const parsed = closeStr ? parseDateForSorting(closeStr) : null;
    if (parsed) return parsed;
  }

  return 0;
}

/**
 * Check if lot is closed
 */
export function isLotClosed(lot: Lot): boolean {
  return utilIsLotClosed(lot, getEffectiveCloseTime);
}
